import { Alert } from "../Alert";
import { FileCard } from "./FileCard";
import type { SearchResponse } from "../../api/types";

/**
 * One search's results: the scope it ran under, what it found, and — before any of that — what it
 * could not do.
 *
 * The ordering is the point. `index_status.nothing_to_search` means the corpus was never searched,
 * so an empty list there must never read as "nothing matched"; and `permissions.hidden_count` means
 * results were withheld from *this* user, which they are entitled to know without being told what
 * was hidden.
 *
 * Only the first `shown` cards are drawn; the rest are one click away on their own page. The count
 * is the administrator's (`results_shown` in the tuning rules), because how much of an answer fits
 * before it stops being readable is a judgement about this deployment's documents, not a constant.
 */
export function SearchResults({
  result,
  shown,
  onViewAll,
}: {
  result: SearchResponse;
  shown: number;
  onViewAll: (result: SearchResponse) => void;
}): JSX.Element {
  const { index_status: status, permissions } = result;
  const visible = result.hits.slice(0, shown);
  const remaining = result.hits.length - visible.length;
  const scope = scopeOf(result);
  return (
    <section className="search-results" aria-label={`Results for ${scope}`}>
      <header className="search-results__scope">
        <span className="search-results__domain">{scope}</span>
        <span>
          {result.files_searched_count.toLocaleString()}{" "}
          {result.files_searched_count === 1 ? "file" : "files"} searched
        </span>
      </header>

      {status.nothing_to_search ? (
        <Alert variant="info">{status.notice || "This has not been indexed yet."}</Alert>
      ) : (
        <>
          {status.notice && <Alert variant="warning">{status.notice}</Alert>}
          {result.hits.length === 0 && (
            <p className="search-results__empty">
              {result.domain_restriction.length > 0
                ? "No files in this scope matched."
                : "No files matched."}
            </p>
          )}
          {visible.map((hit) => (
            <FileCard key={hit.file_id} hit={hit} />
          ))}
          {remaining > 0 && (
            <button
              type="button"
              className="link-button search-results__view-all"
              onClick={() => onViewAll(result)}
            >
              View all {result.hits.length} results ({remaining} more)
            </button>
          )}
          {result.results_truncated && (
            <p className="search-results__withheld">
              The search stopped at {result.result_limit_applied} results, so more files may match.
            </p>
          )}
        </>
      )}

      {permissions.hidden_count > 0 && (
        <p className="search-results__withheld">
          {permissions.hidden_count} further{" "}
          {permissions.hidden_count === 1 ? "result was" : "results were"} withheld because you may
          not open {permissions.hidden_count === 1 ? "it" : "them"}.
          {permissions.notice && ` ${permissions.notice}`}
        </p>
      )}
    </section>
  );
}

/**
 * What this search covered, for the header.
 *
 * A search no longer has to name a domain, so an empty restriction is the common case and has to say
 * something. "All files" rather than a blank: the header's job is to tell the reader how wide the
 * answer is, and nothing at all reads as a missing value instead of a deliberate scope.
 */
function scopeOf(result: SearchResponse): string {
  return result.domain_restriction.length > 0 ? result.domain_restriction.join(", ") : "All files";
}
